<template>
  <div
    v-if="visible"
    class="tw-w-full tw-bg-primary-grey-900 tw-font-mono tw-text-xs tw-text-white"
    data-test-id="flag-debug-banner"
  >
    <!-- Summary bar -->
    <div
      class="tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-1 tw-px-3 tw-py-1.5"
    >
      <button
        type="button"
        class="tw-font-semibold tw-tracking-wide"
        @click="expanded = !expanded"
      >
        🚩 Flags debug {{ expanded ? "▾" : "▸" }}
      </button>

      <span>
        source:
        <b
          :class="
            isHarness ? 'tw-text-branding-orange-400' : 'tw-text-emerald-400'
          "
        >
          {{ source }}
        </b>
      </span>

      <span
        >env: <b>{{ envMode || "—" }}</b></span
      >

      <template v-if="isHarness">
        <span
          >harness: <b>{{ harnessMode }}</b></span
        >
        <span>
          SDK:
          <b :class="sdkReady ? 'tw-text-emerald-400' : 'tw-text-amber-400'">
            {{ sdkReady ? "ready" : "loading…" }}
          </b>
        </span>
        <span
          >key: <b>{{ trafficKey }}</b></span
        >
      </template>

      <span
        >on: <b>{{ enabledCount }}/{{ rows.length }}</b></span
      >

      <button
        type="button"
        class="tw-ml-auto tw-opacity-70 hover:tw-opacity-100"
        title="Hide debug banner"
        @click="visible = false"
      >
        ✕
      </button>
    </div>

    <!-- Full flag table -->
    <div
      v-if="expanded"
      class="tw-overflow-x-auto tw-border-t tw-border-white/10"
    >
      <table class="tw-w-full tw-text-left">
        <thead class="tw-text-white/60">
          <tr>
            <th class="tw-px-3 tw-py-1">flag</th>
            <th class="tw-px-3 tw-py-1">static</th>
            <th v-if="isHarness" class="tw-px-3 tw-py-1">treatment</th>
            <th class="tw-px-3 tw-py-1">resolved</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in rows"
            :key="row.name"
            class="tw-border-t tw-border-white/5"
          >
            <td class="tw-px-3 tw-py-1">{{ row.name }}</td>
            <td class="tw-px-3 tw-py-1" :class="boolClass(row.staticValue)">
              {{ row.staticValue }}
            </td>
            <td
              v-if="isHarness"
              class="tw-px-3 tw-py-1"
              :class="row.treatment === 'control' ? 'tw-text-white/40' : ''"
            >
              {{ row.treatment }}
            </td>
            <td
              class="tw-px-3 tw-py-1 tw-font-semibold"
              :class="boolClass(row.resolved)"
            >
              {{ row.resolved }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import {
  FLAG_NAMES,
  loadFlagsConfig,
  useFlagsConfig,
} from "@/config/flagsConfig";
import {
  getFlagsSource,
  isHarnessFlagsSource,
} from "@/config/flags/flagsSource";
import { getHarnessTreatment } from "@/config/split/harnessFlags";
import { getSplitClient, treatmentToBoolean } from "@/config/split/splitClient";
import {
  getHarnessSdkKey,
  resolveTrafficKey,
} from "@/config/split/harnessConfig";
import { loadRuntimeConfig } from "@/config/runtimeConfig";
import { useAuthenticationStore } from "@/store/authentication.store";

const authStore = useAuthenticationStore();
const flagsConfig = useFlagsConfig();

// Ensure the static config is materialised (App.vue normally loads it already).
try {
  loadFlagsConfig();
} catch {
  /* config not present — rows fall back to `false` */
}

const source = computed(() => getFlagsSource());
const isHarness = computed(() => isHarnessFlagsSource());
const envMode = computed<string | undefined>(() => {
  try {
    return loadRuntimeConfig().VITE_ENV_MODE;
  } catch {
    return undefined;
  }
});
const harnessMode = computed(() =>
  getHarnessSdkKey() ? "network" : "localhost (mock)",
);
const trafficKey = computed(() => resolveTrafficKey(authStore));

const rows = computed(() =>
  FLAG_NAMES.map((name) => {
    const staticValue = flagsConfig.value?.[name] ?? false;
    // getHarnessTreatment registers a reactive dep so this recomputes on
    // SDK_READY / SDK_UPDATE. Mirrors the resolution logic in useFlag.
    const treatment = isHarness.value ? getHarnessTreatment(name) : null;
    const resolved =
      !isHarness.value || treatment === null || treatment === "control"
        ? staticValue
        : treatmentToBoolean(treatment);
    return { name, staticValue, treatment, resolved };
  }),
);

const enabledCount = computed(
  () => rows.value.filter((r) => r.resolved).length,
);

const boolClass = (value: boolean) =>
  value ? "tw-text-emerald-400" : "tw-text-white/50";

// SDK readiness indicator — refreshed on mount and whenever we re-key.
const sdkReady = ref(false);
const refreshSdkReady = () => {
  sdkReady.value = false;
  getSplitClient()
    ?.ready()
    .then(() => (sdkReady.value = true))
    .catch(() => (sdkReady.value = false));
};
onMounted(refreshSdkReady);
watch(trafficKey, refreshSdkReady);

const expanded = ref(false);
const visible = ref(true);
</script>
